import React, { useState, useEffect } from 'react';
import {
    Box, Typography, Button, TextField, Grid, Card, CardContent, LinearProgress, Avatar, AvatarGroup,
    Chip, Dialog, DialogTitle, DialogContent, DialogActions, Select, MenuItem, FormControl, InputLabel,
    InputAdornment, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, ToggleButton, ToggleButtonGroup,
    FormControlLabel, Checkbox, Tooltip, Grow, Divider
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import API_URL from '../config';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import ViewListIcon from '@mui/icons-material/ViewList';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import PersonIcon from '@mui/icons-material/Person';
import Loading from '../components/Loading';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import CloseIcon from '@mui/icons-material/Close';

const getStatusColor = (status) => {
    switch (status) {
        case 'Active': return 'primary';
        case 'Completed': return 'success';
        case 'In Progress': return 'primary';
        case 'On Hold': return 'warning';
        default: return 'default';
    }
};

const getPriorityColor = (priority) => {
    switch (priority) {
        case 'High': return '#DC3545';
        case 'Medium': return '#FFB020';
        case 'Low': return '#00C292';
        default: return '#6C757D';
    }
};

const calculateProgress = (tasks) => {
    if (!tasks || tasks.length === 0) return 0;
    const completed = tasks.filter(t => t.completed).length;
    return Math.round((completed / tasks.length) * 100);
};

function ITProjects() {
    const [projects, setProjects] = useState([]);
    const [countries, setCountries] = useState([]);
    const [loading, setLoading] = useState(true);
    const [open, setOpen] = useState(false);
    const [newProject, setNewProject] = useState({
        name: '', description: '', status: 'Active', priority: 'Medium', time_limit: '', start_date: '', country_id: ''
    });
    const [viewMode, setViewMode] = useState('grid');
    const [filter, setFilter] = useState('All');
    const [search, setSearch] = useState('');
    const navigate = useNavigate();
    const token = localStorage.getItem('token');
    const headers = { Authorization: `Bearer ${token}` };

    useEffect(() => {
        fetchProjects();
        fetchCountries();
    }, [search]);

    const fetchCountries = async () => {
        try {
            const res = await axios.get(`${API_URL}/setup/countries`, { headers });
            setCountries(res.data);
        } catch (e) { console.error(e); }
    };

    const fetchProjects = async () => {
        try {
            const res = await axios.get(`${API_URL}/it-projects`, { headers });
            setProjects(res.data);
        } catch (e) { console.error(e); }
        finally { setLoading(false); }
    };

    const handleCreate = async () => {
        try {
            const response = await axios.post(`${API_URL}/it-projects`, newProject, { headers });
            setOpen(false);
            navigate(`/it-projects/${response.data.id}`);
        } catch (e) { console.error(e); }
    };

    // Helper functions moved outside

    const handleExportAll = async () => {
        try {
            let query = `?status=${filter}&search=${search}`;
            const response = await axios.get(`${API_URL}/it-projects/export-all-pdf${query}`, {
                headers,
                responseType: 'blob'
            });
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `Global_Projects_Report_${new Date().toISOString().slice(0, 10)}.pdf`);
            document.body.appendChild(link);
            link.click();
            link.remove();
        } catch (e) {
            console.error("Export failed", e);
            const msg = e.response?.data?.error || 'Failed to export Global PDF';
            try {
                if (e.response?.data instanceof Blob) {
                    const text = await e.response.data.text();
                    const json = JSON.parse(text);
                    if (json.error) alert(json.error);
                    else alert(msg);
                } else {
                    alert(msg);
                }
            } catch {
                alert(msg);
            }
        }
    };

    const [assignedToMe, setAssignToMe] = useState(true);
    const [selectedCountries, setSelectedCountries] = useState([]);

    // Get current user from token
    const getCurrentUser = () => {
        if (!token) return null;
        try {
            return JSON.parse(atob(token.split('.')[1]));
        } catch (e) {
            return null;
        }
    };
    const currentUser = getCurrentUser();

    // ... existing useEffect ... (no change needed here really, but context)

    const filtered = projects.filter(p => {
        const matchesSearch = p.name.toLowerCase().includes(search.toLowerCase()) ||
            (p.ref_number && p.ref_number.toLowerCase().includes(search.toLowerCase()));

        const matchesStatus = filter === 'All' || p.status === filter;
        const matchesCountry = selectedCountries.length === 0 || selectedCountries.includes(p.country_name);
        const matchesAssign = !assignedToMe || (p.assigned_users && p.assigned_users.some(u => u.id === currentUser?.user_id));

        return matchesSearch && matchesStatus && matchesCountry && matchesAssign;
    });

    if (loading) return <Loading message="Loading IT Projects..." />;

    return (
        <Box sx={{ width: '100%', overflowX: 'hidden', boxSizing: 'border-box', pb: 6, px: { xs: 1, md: 3 }, bgcolor: '#f4f6f8', minHeight: '100vh' }}>
          <Grow in={true} timeout={500}>
            <Box sx={{ maxWidth: 1400, mx: 'auto' }}>
            {/* Header */}
            <Box sx={{ 
                mb: 4, 
                mt: 2, 
                p: 4, 
                borderRadius: 4, 
                background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)', 
                color: 'white',
                boxShadow: '0 10px 30px rgba(30, 60, 114, 0.2)',
                position: 'relative',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                justifyContent: 'space-between',
                alignItems: { xs: 'flex-start', md: 'center' },
                gap: 3
            }}>
                <Box sx={{ position: 'relative', zIndex: 2 }}>
                    <Typography variant="h4" fontWeight="800" sx={{ mb: 1, letterSpacing: 0.5, display: 'flex', alignItems: 'center', gap: 2, color: 'white' }}>
                        <ViewModuleIcon sx={{ fontSize: 36, color: '#90caf9' }} />
                        IT Projects
                    </Typography>
                    <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                        Manage your global projects and team assignments
                    </Typography>
                </Box>
                
                <Button 
                    variant="contained" 
                    startIcon={<AddIcon />} 
                    onClick={() => setOpen(true)}
                    sx={{ 
                        position: 'relative',
                        zIndex: 2,
                        borderRadius: 2, 
                        fontWeight: 'bold', 
                        px: 3, 
                        py: 1,
                        bgcolor: 'white',
                        color: '#1e3c72',
                        boxShadow: '0 4px 14px rgba(0,0,0,0.1)',
                        '&:hover': { bgcolor: '#f1f5f9', transform: 'translateY(-2px)', boxShadow: '0 6px 20px rgba(0,0,0,0.15)' }
                    }}
                >
                    New Project
                </Button>
                
                {/* Decorative background circles */}
                <Box sx={{ position: 'absolute', top: -50, right: -50, width: 200, height: 200, borderRadius: '50%', background: 'rgba(255,255,255,0.05)' }} />
                <Box sx={{ position: 'absolute', bottom: -100, right: 100, width: 250, height: 250, borderRadius: '50%', background: 'rgba(255,255,255,0.05)' }} />
            </Box>

            {/* Filters and Controls */}
            <Card elevation={0} sx={{ mb: 4, p: 2, borderRadius: 4, border: '1px solid #f1f5f9', boxShadow: '0 4px 20px rgba(0,0,0,0.03)' }}>
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', lg: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'stretch', lg: 'center' }, gap: 2 }}>
                    
                    {/* Left: Filters */}
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                        <Chip
                            icon={<PersonIcon />}
                            label="My Projects"
                            onClick={() => setAssignToMe(!assignedToMe)}
                            sx={{ 
                                fontWeight: 'bold', 
                                borderRadius: 2, 
                                px: 1,
                                height: 32,
                                bgcolor: assignedToMe ? '#eff6ff' : '#f8fafc',
                                color: assignedToMe ? '#2563eb' : '#64748b',
                                border: '1px solid',
                                borderColor: assignedToMe ? '#bfdbfe' : '#e2e8f0',
                                '&:hover': { bgcolor: assignedToMe ? '#dbeafe' : '#f1f5f9' },
                                transition: 'all 0.2s ease'
                            }}
                        />
                        <Divider orientation="vertical" flexItem sx={{ mx: 1, height: 24, alignSelf: 'center' }} />
                        {['All', 'Active', 'In Progress', 'Completed', 'On Hold'].map(f => (
                            <Chip 
                                key={f} 
                                label={f} 
                                onClick={() => setFilter(f)} 
                                sx={{ 
                                    fontWeight: '600', 
                                    borderRadius: 2,
                                    height: 32,
                                    bgcolor: filter === f ? '#1e293b' : 'transparent',
                                    color: filter === f ? 'white' : '#64748b',
                                    '&:hover': { bgcolor: filter === f ? '#334155' : '#f1f5f9' },
                                    transition: 'all 0.2s ease'
                                }}
                            />
                        ))}
                    </Box>

                    {/* Right: Actions */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', justifyContent: { xs: 'flex-start', lg: 'flex-end' } }}>
                        <FormControl size="small" sx={{ minWidth: 200 }}>
                            <Select
                                multiple
                                value={selectedCountries}
                                onChange={(e) => setSelectedCountries(typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)}
                                displayEmpty
                                variant="outlined"
                                renderValue={(selected) => {
                                    if (selected.length === 0) {
                                        return <Typography sx={{ fontSize: '0.875rem', color: '#64748b', fontWeight: '500' }}>All Countries</Typography>;
                                    }
                                    return selected.join(', ');
                                }}
                                sx={{ borderRadius: 2, bgcolor: '#f8fafc', '& fieldset': { borderColor: '#e2e8f0' } }}
                            >
                                <MenuItem disabled value="">
                                    <Typography variant="body2">Select Countries</Typography>
                                </MenuItem>
                                {countries.map(c => (
                                    <MenuItem key={c.id} value={c.name}>
                                        <Checkbox checked={selectedCountries.indexOf(c.name) > -1} size="small" />
                                        {c.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <TextField
                            placeholder="Search projects..."
                            size="small"
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                            InputProps={{
                                startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: '#94a3b8' }} /></InputAdornment>,
                                sx: { borderRadius: 3, bgcolor: '#f8fafc', '& fieldset': { borderColor: '#e2e8f0' } }
                            }}
                            sx={{ width: { xs: '100%', sm: 250 } }}
                        />

                        <Button
                            variant="outlined"
                            startIcon={<PictureAsPdfIcon />}
                            onClick={handleExportAll}
                            sx={{ 
                                borderRadius: 3, 
                                whiteSpace: 'nowrap', 
                                textTransform: 'none', 
                                color: '#1e293b', 
                                borderColor: '#e2e8f0',
                                fontWeight: '600',
                                '&:hover': { bgcolor: '#f8fafc' }
                            }}
                        >
                            Export PDF
                        </Button>

                        <ToggleButtonGroup
                            value={viewMode}
                            exclusive
                            onChange={(e, newMode) => { if (newMode) setViewMode(newMode); }}
                            size="small"
                            sx={{ '& .MuiToggleButton-root': { borderRadius: 2, border: '1px solid #e2e8f0' } }}
                        >
                            <ToggleButton value="grid" aria-label="grid view">
                                <ViewModuleIcon fontSize="small" />
                            </ToggleButton>
                            <ToggleButton value="list" aria-label="list view">
                                <ViewListIcon fontSize="small" />
                            </ToggleButton>
                        </ToggleButtonGroup>
                    </Box>
                </Box>
            </Card>

            {viewMode === 'grid' ? (
                <Grid container spacing={3}>
                    {filtered.map(project => (
                        <Grid item xs={12} md={4} lg={3} key={project.id}>
                            <Card 
                                elevation={0} 
                                sx={{ 
                                    height: '100%', 
                                    borderRadius: 4, 
                                    border: '1px solid #f1f5f9',
                                    boxShadow: '0 4px 20px rgba(0,0,0,0.03)', 
                                    cursor: 'pointer', 
                                    transition: 'all 0.3s ease', 
                                    display: 'flex',
                                    flexDirection: 'column',
                                    '&:hover': { transform: 'translateY(-6px)', boxShadow: '0 12px 28px rgba(0,0,0,0.08)', borderColor: '#cbd5e1' } 
                                }} 
                                onClick={() => navigate(`/it-projects/${project.id}`)}
                            >
                                <CardContent sx={{ p: 3, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, overflow: 'hidden' }}>
                                            <Avatar sx={{ 
                                                bgcolor: '#eff6ff', 
                                                color: '#2563eb', 
                                                width: 48, 
                                                height: 48, 
                                                fontWeight: '800',
                                                fontSize: '1.25rem',
                                                boxShadow: '0 4px 10px rgba(37,99,235,0.15)'
                                            }}>
                                                {project.name ? project.name.substring(0,2).toUpperCase() : 'IT'}
                                            </Avatar>
                                            <Box sx={{ overflow: 'hidden' }}>
                                                <Typography variant="caption" sx={{ fontWeight: '800', display: 'block', color: '#3b82f6', letterSpacing: '0.05em', mb: 0.25 }}>{project.ref_number}</Typography>
                                                <Typography variant="h6" fontWeight="800" sx={{ fontSize: '1.1rem', color: '#1e293b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', mb: 0.25 }}>{project.name}</Typography>
                                                <Typography variant="caption" fontWeight="600" sx={{ color: '#64748b' }}>{project.country_name || 'Global'}</Typography>
                                            </Box>
                                        </Box>
                                        <Chip 
                                            label={project.status} 
                                            size="small" 
                                            color={getStatusColor(project.status)} 
                                            sx={{ 
                                                height: 24, 
                                                fontSize: '0.75rem', 
                                                flexShrink: 0, 
                                                fontWeight: 'bold', 
                                                borderRadius: 1.5,
                                                bgcolor: project.status === 'Completed' ? '#dcfce7' : 
                                                         project.status === 'Active' ? '#dbeafe' : 
                                                         project.status === 'In Progress' ? '#fef3c7' : '#f1f5f9',
                                                color: project.status === 'Completed' ? '#15803d' : 
                                                       project.status === 'Active' ? '#1d4ed8' : 
                                                       project.status === 'In Progress' ? '#b45309' : '#475569',
                                            }} 
                                        />
                                    </Box>

                                    <Box sx={{ mt: 'auto' }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: getPriorityColor(project.priority) }} />
                                                <Typography variant="caption" fontWeight="600" sx={{ color: '#334155' }}>{project.priority}</Typography>
                                            </Box>
                                            <Typography variant="caption" fontWeight="600" sx={{ color: '#64748b' }}>
                                                Due: {project.time_limit ? new Date(project.time_limit).toLocaleDateString() : 'N/A'}
                                            </Typography>
                                        </Box>

                                        <Box sx={{ mb: 3 }}>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                                <Typography variant="caption" fontWeight="700" sx={{ color: '#64748b' }}>Progress</Typography>
                                                <Typography variant="caption" fontWeight="800" sx={{ color: '#1e293b' }}>{calculateProgress(project.tasks)}%</Typography>
                                            </Box>
                                            <LinearProgress 
                                                variant="determinate" 
                                                value={calculateProgress(project.tasks)} 
                                                sx={{ 
                                                    height: 8, 
                                                    borderRadius: 4, 
                                                    bgcolor: '#f1f5f9', 
                                                    '& .MuiLinearProgress-bar': { borderRadius: 4, backgroundImage: 'linear-gradient(45deg, #3b82f6 0%, #60a5fa 100%)' } 
                                                }} 
                                            />
                                        </Box>

                                        <Box sx={{ pt: 2, borderTop: '1px dashed #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <Typography variant="caption" fontWeight="700" sx={{ color: '#64748b' }}>Assignees</Typography>
                                            <AvatarGroup max={4} sx={{ '& .MuiAvatar-root': { width: 32, height: 32, fontSize: '0.8rem', borderColor: 'white', borderWidth: 2 } }}>
                                                {project.assigned_users && project.assigned_users.map(u => (
                                                    <Avatar key={u.id} alt={u.username} sx={{ bgcolor: '#eff6ff', color: '#3b82f6', fontWeight: 'bold' }} title={u.full_name || u.username}>
                                                        {u.username.charAt(0).toUpperCase()}
                                                    </Avatar>
                                                ))}
                                            </AvatarGroup>
                                        </Box>
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
            ) : (
                <Grow in={true} timeout={1200}>
                <Paper elevation={0} sx={{ borderRadius: 4, overflow: 'hidden', border: 'none', boxShadow: '0 10px 40px rgba(0,0,0,0.06)', width: '100%', bgcolor: 'white' }}>
                  <Box sx={{ overflowX: 'auto', width: '100%', display: 'block' }}>
                    <Table sx={{ width: '100%', tableLayout: 'auto', '& .MuiTableCell-root': { borderColor: '#f0f0f0', py: 2.5 } }} aria-label="it projects table">
                        <TableHead sx={{ bgcolor: '#f8fafc' }}>
                            <TableRow>
                                <TableCell sx={{ fontWeight: '800', color: '#475569', textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: '0.5px' }}>Project / Reference</TableCell>
                                <TableCell sx={{ fontWeight: '800', color: '#475569', textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: '0.5px' }}>Country</TableCell>
                                <TableCell sx={{ fontWeight: '800', color: '#475569', textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: '0.5px' }}>Team</TableCell>
                                <TableCell sx={{ fontWeight: '800', color: '#475569', textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: '0.5px' }}>Tasks</TableCell>
                                <TableCell sx={{ fontWeight: '800', color: '#475569', textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: '0.5px' }}>Status</TableCell>
                                <TableCell sx={{ fontWeight: '800', color: '#475569', textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: '0.5px' }}>Due Date</TableCell>
                                <TableCell sx={{ fontWeight: '800', color: '#475569', textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: '0.5px' }}>Progress</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filtered.map(p => (
                                <ProjectRow key={p.id} project={p} navigate={navigate} />
                            ))}
                            {filtered.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={7} align="center" sx={{ py: 6, color: '#94a3b8', fontWeight: 600 }}>
                                        No IT projects found matching your criteria.
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                  </Box>
                </Paper>
                </Grow>
            )}

            {/* Add Project Dialog */}
            <Dialog 
                open={open} 
                onClose={() => setOpen(false)} 
                maxWidth="md" 
                fullWidth
                PaperProps={{ sx: { borderRadius: 4, overflow: 'hidden' } }}
            >
                <DialogTitle sx={{ bgcolor: '#1e3c72', color: 'white', fontWeight: 'bold', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    Add New IT Project
                    <IconButton size="small" onClick={() => setOpen(false)} sx={{ color: 'white' }}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent sx={{ p: { xs: 2, sm: 4 }, bgcolor: '#f8fafc' }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 1 }}>
                        <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid #e2e8f0' }}>
                            <Typography variant="subtitle2" sx={{ mb: 3, fontWeight: 800, color: '#64748b', textTransform: 'uppercase' }}>Basic Information</Typography>
                            <Grid container spacing={2}>
                                <Grid item xs={12}>
                                    <TextField fullWidth label="Project Name" value={newProject.name} onChange={e => setNewProject({ ...newProject, name: e.target.value })} />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField fullWidth label="Description" multiline rows={3} value={newProject.description} onChange={e => setNewProject({ ...newProject, description: e.target.value })} />
                                </Grid>
                            </Grid>
                        </Paper>

                        <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid #e2e8f0' }}>
                            <Typography variant="subtitle2" sx={{ mb: 3, fontWeight: 800, color: '#64748b', textTransform: 'uppercase' }}>Details & Timeline</Typography>
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={6}>
                                    <FormControl fullWidth>
                                        <InputLabel>Status</InputLabel>
                                        <Select value={newProject.status} label="Status" onChange={e => setNewProject({ ...newProject, status: e.target.value })}>
                                            <MenuItem value="Active">Active</MenuItem>
                                            <MenuItem value="In Progress">In Progress</MenuItem>
                                            <MenuItem value="Completed">Completed</MenuItem>
                                            <MenuItem value="On Hold">On Hold</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <FormControl fullWidth>
                                        <InputLabel>Priority</InputLabel>
                                        <Select value={newProject.priority} label="Priority" onChange={e => setNewProject({ ...newProject, priority: e.target.value })}>
                                            <MenuItem value="High">High</MenuItem>
                                            <MenuItem value="Medium">Medium</MenuItem>
                                            <MenuItem value="Low">Low</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12}>
                                    <FormControl fullWidth>
                                        <InputLabel>Country</InputLabel>
                                        <Select value={newProject.country_id} label="Country" onChange={e => setNewProject({ ...newProject, country_id: e.target.value })}>
                                            <MenuItem value=""><em>Global</em></MenuItem>
                                            {countries.map(c => (
                                                <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Start Date"
                                        type="date"
                                        InputLabelProps={{ shrink: true }}
                                        value={newProject.start_date}
                                        onChange={e => setNewProject({ ...newProject, start_date: e.target.value })}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Deadline"
                                        type="date"
                                        InputLabelProps={{ shrink: true }}
                                        value={newProject.time_limit}
                                        onChange={e => setNewProject({ ...newProject, time_limit: e.target.value })}
                                    />
                                </Grid>
                            </Grid>
                        </Paper>
                    </Box>
                </DialogContent>
                <DialogActions sx={{ p: 3, pt: 2, bgcolor: '#f8fafc', borderTop: '1px solid #e2e8f0' }}>
                    <Button onClick={() => setOpen(false)} sx={{ borderRadius: 2, fontWeight: '600', color: '#64748b' }}>Cancel</Button>
                    <Button onClick={handleCreate} variant="contained" sx={{ borderRadius: 2, fontWeight: 'bold', px: 4, py: 1.5, boxShadow: '0 4px 14px rgba(30, 60, 114, 0.2)' }}>
                        Create Project
                    </Button>
                </DialogActions>
            </Dialog>
            </Box>
          </Grow>
        </Box>
    );
}

export default ITProjects;

function ProjectRow({ project, navigate }) {
    const isCompleted = project.status === 'Completed';
    const [openTasks, setOpenTasks] = useState(false);

    return (
        <React.Fragment>
        <TableRow 
            hover
            onClick={() => navigate(`/it-projects/${project.id}`)}
            sx={{ 
                cursor: 'pointer', 
                transition: 'background-color 0.2s, transform 0.1s', 
                '&:hover': { bgcolor: '#f8fbff !important' },
                '& > *': { borderBottom: 'unset !important' }
            }}
        >
            <TableCell component="th" scope="row">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5 }}>
                    <Avatar sx={{ 
                        bgcolor: '#eff6ff', 
                        color: '#2563eb', 
                        fontWeight: '900', 
                        width: 46, height: 46, 
                        boxShadow: '0 4px 10px rgba(37,99,235,0.2)'
                    }}>
                        {project.name ? project.name.substring(0,2).toUpperCase() : 'IT'}
                    </Avatar>
                    <Box>
                        <Typography fontWeight="700" sx={{ color: '#1e293b', fontSize: '0.95rem' }}>{project.name}</Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                            <Typography variant="caption" sx={{ fontWeight: '800', color: '#3b82f6', bgcolor: '#eff6ff', px: 1, py: 0.25, borderRadius: 1.5 }}>{project.ref_number}</Typography>
                            {project.priority && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: getPriorityColor(project.priority) }} />
                                    <Typography variant="caption" fontWeight="600" color="#94a3b8">{project.priority}</Typography>
                                </Box>
                            )}
                        </Box>
                    </Box>
                </Box>
            </TableCell>

            <TableCell sx={{ color: '#334155', fontWeight: 600, fontSize: '0.85rem' }}>
                {project.country_name || 'Global'}
            </TableCell>

            <TableCell>
                <AvatarGroup max={4} sx={{ justifyContent: 'flex-start', '& .MuiAvatar-root': { width: 32, height: 32, fontSize: '0.8rem', borderColor: 'white', borderWidth: 2 } }}>
                    {project.assigned_users?.map((u, i) => (
                        <Avatar key={u.id} alt={u.username} sx={{ bgcolor: '#eff6ff', color: '#3b82f6', fontWeight: 'bold' }}>
                            {u.username.charAt(0).toUpperCase()}
                        </Avatar>
                    ))}
                </AvatarGroup>
            </TableCell>

            <TableCell onClick={(e) => { e.stopPropagation(); setOpenTasks(!openTasks); }} sx={{ cursor: 'pointer' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 0.5, borderRadius: 1.5, '&:hover': { bgcolor: '#f1f5f9' }, width: 'fit-content' }}>
                    <IconButton size="small" sx={{ color: openTasks ? '#2563eb' : '#64748b' }}>
                        {openTasks ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
                    </IconButton>
                    <CheckCircleIcon sx={{ fontSize: 18, color: isCompleted ? '#22c55e' : '#cbd5e1' }} />
                    <Typography variant="body2" sx={{ fontWeight: 800, color: isCompleted ? '#22c55e' : '#64748b' }}>
                        {project.tasks ? project.tasks.filter(t => t.completed || t.status === 'Completed').length : 0}/
                        {project.tasks ? project.tasks.length : 0}
                    </Typography>
                </Box>
            </TableCell>

            <TableCell>
                <Chip 
                    label={project.status} 
                    size="small" 
                    color={getStatusColor(project.status)} 
                    sx={{ 
                        height: 26, 
                        fontSize: '0.75rem', 
                        fontWeight: 'bold', 
                        borderRadius: '6px', 
                        px: 1 
                    }} 
                />
            </TableCell>

            <TableCell sx={{ color: '#64748b', fontWeight: 600, fontSize: '0.85rem' }}>
                <Typography variant="body2" fontWeight="700" color={isCompleted ? '#94a3b8' : '#334155'}>
                    {project.time_limit ? new Date(project.time_limit).toLocaleDateString() : 'N/A'}
                </Typography>
            </TableCell>

            <TableCell sx={{ minWidth: 150 }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Box sx={{ width: '100%', mr: 1.5 }}>
                        <LinearProgress 
                            variant="determinate" 
                            value={calculateProgress(project.tasks)} 
                            sx={{ 
                                height: 8, 
                                borderRadius: 4, 
                                bgcolor: '#f1f5f9',
                                '& .MuiLinearProgress-bar': { borderRadius: 4, backgroundImage: 'linear-gradient(45deg, #3b82f6 0%, #60a5fa 100%)' }
                            }} 
                        />
                    </Box>
                    <Box sx={{ minWidth: 40 }}>
                        <Typography variant="caption" fontWeight="800" color="#1e293b">{`${Math.round(calculateProgress(project.tasks))}%`}</Typography>
                    </Box>
                </Box>
            </TableCell>
        </TableRow>
        <TableRow>
            <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={7}>
                <Collapse in={openTasks} timeout="auto" unmountOnExit>
                    <Box sx={{ margin: 2, ml: 6, p: 2, bgcolor: '#f8fafc', borderRadius: 2, border: '1px dashed #cbd5e1' }}>
                        <Typography variant="subtitle2" gutterBottom component="div" sx={{ fontWeight: '800', color: '#475569', mb: 2 }}>
                            Task List ({project.tasks ? project.tasks.length : 0})
                        </Typography>
                        {project.tasks && project.tasks.length > 0 ? (
                            <Table size="small" aria-label="tasks">
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 'bold', color: '#64748b' }}>Status</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold', color: '#64748b' }}>Title</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold', color: '#64748b' }}>Due Date</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold', color: '#64748b' }}>Assigned To</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {project.tasks.map((task) => (
                                        <TableRow key={task.id} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                                            <TableCell component="th" scope="row">
                                                {task.completed || task.status === 'Completed' ? 
                                                    <CheckCircleIcon color="success" fontSize="small" /> : 
                                                    <RadioButtonUncheckedIcon sx={{ color: '#cbd5e1' }} fontSize="small" />
                                                }
                                            </TableCell>
                                            <TableCell sx={{ fontWeight: '600', color: '#334155' }}>{task.title}</TableCell>
                                            <TableCell sx={{ color: '#64748b', fontSize: '0.85rem' }}>{task.due_date ? new Date(task.due_date).toLocaleDateString() : '-'}</TableCell>
                                            <TableCell>
                                                <Typography variant="body2" sx={{ fontWeight: '600', color: '#3b82f6', bgcolor: '#eff6ff', display: 'inline-block', px: 1, py: 0.25, borderRadius: 1 }}>
                                                    {task.assigned_to_name || '-'}
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        ) : (
                            <Typography variant="body2" color="#94a3b8" sx={{ fontStyle: 'italic' }}>No tasks have been assigned to this project yet.</Typography>
                        )}
                    </Box>
                </Collapse>
            </TableCell>
        </TableRow>
        </React.Fragment>
    );
}
